/** Mirrors the backend schema's `.max(2048)`. */
export const LINKEDIN_URL_MAX_LEN = 2048;

const NON_HTTP_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_SCHEME = /^https?:\/\//i;

/**
 * Lenient URL shape shared by the edit form (validation) and the header card
 * (link target). Mirrors viq-backend `shared/src/types/person.ts`
 * `isLinkedinUrlLike` — keep the two in sync.
 *
 * Optional http(s) scheme, a host with at least one dot, no whitespace. It
 * does NOT require a linkedin.com host — the mobile app stores whatever the
 * rep typed and card extraction yields bare paths like `linkedin.com/in/jane`;
 * the admin side must never refuse to save a value the phone already holds.
 * Uses the URL parser rather than a regex so it runs in linear time and so a
 * non-http(s) scheme (javascript:, vbscript:, data:) is rejected outright.
 * Returns false for '' — callers must special-case a cleared field
 * (isValidLinkedinUrl does).
 */
export function isLinkedinUrlLike(v: string): boolean {
  if (/\s/.test(v)) return false;
  if (/^[/\\]/.test(v)) return false;
  if (NON_HTTP_SCHEME.test(v) && !HTTP_SCHEME.test(v)) return false;
  try {
    const u = new URL(HTTP_SCHEME.test(v) ? v : `https://${v}`);
    return (
      (u.protocol === 'http:' || u.protocol === 'https:') &&
      u.hostname.includes('.') &&
      !u.hostname.startsWith('.') &&
      !u.hostname.endsWith('.')
    );
  } catch {
    return false;
  }
}

/**
 * Empty is valid (it clears the field); otherwise the trimmed value must be
 * URL-like. Trimming first mirrors the backend schema, which trims before it
 * validates — a pasted URL often carries a trailing space.
 */
export function isValidLinkedinUrl(value: string): boolean {
  const v = value.trim();
  return v === '' || isLinkedinUrlLike(v);
}

/**
 * The href for a stored value. Bare host paths get an `https://` prefix so the
 * anchor is absolute — a scheme-less href renders as a RELATIVE link to the
 * SPA's own origin and 404s. The visible text stays exactly as stored.
 *
 * The prefix is UNCONDITIONAL for anything not already starting with
 * http(s)://, and that is what keeps the output safe as an anchor href even
 * for a value that reached the column through mobile sync without passing the
 * validator: `javascript:alert(1.0)` becomes the inert
 * `https://javascript:alert(1.0)`. Do not "improve" this to skip the prefix
 * whenever some scheme is present. Trims first because sync-written rows may
 * carry surrounding whitespace. Call only with a non-empty stored value; the
 * card guards the empty case.
 *
 * This guarantees only the SCHEME, never the destination host — a stored
 * value may point anywhere, by design (the validator is host-agnostic).
 */
export function toLinkedinHref(value: string): string {
  const v = value.trim();
  return HTTP_SCHEME.test(v) ? v : `https://${v}`;
}
